// src/components/StatisticsEmbeds.tsx

import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Divider,
  Skeleton,
} from "@mui/material";
import { useUserStore } from "@/hooks/useUserStore";
import { useGetEventLogs } from "@/hooks/useEventLogs";

const { QlikEmbed } = require("@qlik/embed-react");

interface StatisticsEmbedsProps {
  objectIds: [string, string, string, string, string];
  enrollmentId: string;
  participantId: string;
}

const StatisticsEmbeds: React.FC<StatisticsEmbedsProps> = ({
  objectIds,
  enrollmentId,
  participantId,
}) => {
  const APP_ID = process.env.REACT_APP_QLIK_APP_ID || "";
  const { userInfo } = useUserStore();

  // Fetch event logs via our custom hook
  const {
    data: logs,
    isLoading: logsLoading,
    error: logsError,
  } = useGetEventLogs(participantId, enrollmentId);

  const [showAllLogs, setShowAllLogs] = useState(false);
  const [qlikApi, setQlikApi] = useState<any>(null);

  useEffect(() => {
    if (!qlikApi) return;

    let isMounted = true;
    qlikApi
      .getDoc()
      .then(({ app }: any) => {
        if (!isMounted) return;
        app.clearAll();

        return app.getField("enrollment_id").then((f: any) => {
          return f.selectValues([{ qText: enrollmentId }], false, false);
        });
      })
      .catch(console.error);

    return () => {
      isMounted = false;
    };
  }, [qlikApi, enrollmentId]);

  const EmbedWrapper: React.FC<{ objectId: string; height: string }> = ({
    objectId,
    height,
  }) => (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        <CircularProgress size={24} />
      </Box>
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
        }}
      >
        <QlikEmbed
          ui="analytics/chart"
          appId={APP_ID}
          objectId={objectId}
          identity={userInfo?._id}
          style={{ width: "100%", height: "100%" }}
        />
      </Box>
    </Box>
  );

  const EventLogList: React.FC = () => {
    if (logsLoading) {
      return (
        <>
          <Skeleton variant="text" width="80%" height={30} />
          <Skeleton variant="text" width="90%" height={30} />
          <Skeleton variant="text" width="70%" height={30} />
        </>
      );
    }

    if (logsError) {
      return (
        <Typography color="error">
          Failed to load event logs. Please try again.
        </Typography>
      );
    }

    if (!logs || logs.length === 0) {
      return (
        <Typography>No activity logs found for this participant.</Typography>
      );
    }

    const previewCount = 3;
    const countToShow = showAllLogs
      ? logs.length
      : Math.min(previewCount, logs.length);

    return (
      <Box>
        <List disablePadding>
          {logs.slice(0, countToShow).map((log) => (
            <Box key={log._id}>
              <ListItem alignItems="flex-start" disableGutters>
                <ListItemText
                  primary={log.eventDescription}
                  secondary={new Date(log.eventDate).toLocaleString()}
                />
              </ListItem>
              <Divider component="li" />
            </Box>
          ))}
        </List>

        {logs.length > previewCount && (
          <Box textAlign="center" mt={1}>
            <Button
              size="small"
              onClick={() => setShowAllLogs((prev) => !prev)}
            >
              {showAllLogs ? "Show Less" : `See All (${logs.length})`}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        gap: 2,
      }}
    >
      <Box sx={{ flexBasis: "40%", minWidth: 0 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "500px",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={24} />
          </Box>
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              height: "100%",
            }}
          >
            <QlikEmbed
              ui="analytics/chart"
              appId={APP_ID}
              objectId={objectIds[0]}
              identity={userInfo?._id}
              style={{ width: "100%", height: "100%" }}
              exposeRefApi={setQlikApi}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ flexBasis: "20%", minWidth: 0 }}>
        <EmbedWrapper objectId={objectIds[1]} height="500px" />
      </Box>

      <Box
        sx={{
          flexBasis: "20%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <EmbedWrapper objectId={objectIds[2]} height="245px" />
        <EmbedWrapper objectId={objectIds[3]} height="245px" />
      </Box>

      <Box
        sx={{
          flexBasis: "20%",
          minWidth: 0,
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          p: 1,
          maxHeight: "500px",
          overflowY: "auto",
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
          Activity Log
        </Typography>
        <EventLogList />
      </Box>
    </Box>
  );
};

export default StatisticsEmbeds;
